import React from "react";
import PropTypes from "prop-types";
import Skeleton from "./Skeleton";
import CardSkeleton from "./CardSkeleton";
import TableSkeleton from "./TableSkeleton";

export default function PageSkeleton({
  type = "register",
  variant = "default",
  className = "",
  style = {},
  label = "Loading content…",
  ...rest
}) {
  if (type === "detail" && variant === "contact") {
    return (
      <div
        className={`app-skeleton-page app-skeleton-contact-page ${className}`.trim()}
        style={style}
        role="status"
        aria-busy="true"
        aria-label={label}
        {...rest}
      >
        <span className="skeleton-sr-only">{label}</span>
        <div className="app-skeleton-contact-header">
          <div className="app-skeleton-contact-back">
            <Skeleton width={138} height={16} radius={4} />
          </div>
          <div className="app-skeleton-page-header">
            <div className="app-skeleton-page-header-titles">
              <div className="app-skeleton-contact-name">
                <Skeleton width={210} height="80%" radius={6} />
              </div>
              <div className="app-skeleton-inline-actions">
                <Skeleton width={84} height={24} radius={999} />
                <Skeleton width={68} height={24} radius={999} />
              </div>
            </div>
            <div className="app-skeleton-page-header-actions">
              <Skeleton width={62} height={40} radius={10} />
              <Skeleton width={116} height={40} radius={10} />
            </div>
          </div>
        </div>
        <div className="app-skeleton-contact-tabs">
          {[68, 60, 54].map((width, index) => (
            <div className="app-skeleton-contact-tab" key={index}>
              <Skeleton width={width} height={14} radius={4} />
            </div>
          ))}
        </div>
        <div className="app-skeleton-contact-stats">
          <CardSkeleton variant="stat" count={3} />
        </div>
        <div className="app-skeleton-contact-overview">
          <div className="app-skeleton-document-main">
            {Array.from({ length: 4 }, (_, card) => (
              <div className="app-skeleton-card-panel" key={card}>
                <div className="app-skeleton-contact-card-title">
                  <Skeleton width="42%" height={18} radius={5} />
                </div>
                <div className="app-skeleton-contact-detail-list">
                  {Array.from({ length: 3 }, (__, row) => (
                    <div className="app-skeleton-summary-row" key={row}>
                      <Skeleton width="26%" height={12} radius={4} />
                      <Skeleton width="38%" height={14} radius={4} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <aside className="app-skeleton-document-sidebar">
            <div className="app-skeleton-card-panel">
              <div className="app-skeleton-contact-card-title">
                <Skeleton width="30%" height={18} radius={5} />
              </div>
              <div className="app-skeleton-contact-detail-list">
                {[0, 1].map((row) => (
                  <div className="app-skeleton-summary-row" key={row}>
                    <Skeleton width="26%" height={12} radius={4} />
                    <Skeleton width="38%" height={14} radius={4} />
                  </div>
                ))}
              </div>
            </div>
            <div className="app-skeleton-card-panel">
              <div className="app-skeleton-contact-card-title">
                <Skeleton width="56%" height={18} radius={5} />
              </div>
              <div className="app-skeleton-contact-activity">
                {[0, 1, 2].map((row) => (
                  <div className="app-skeleton-contact-activity-row" key={row}>
                    <Skeleton variant="circular" width={24} height={24} />
                    <div className="app-skeleton-contact-activity-text">
                      <Skeleton width="64%" height={14} radius={4} />
                      <Skeleton width="88%" height={12} radius={4} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>
    );
  }

  if (type === "editor" && variant === "expense") {
    return (
      <div
        className={`app-skeleton-page app-skeleton-expense-page ${className}`.trim()}
        style={style}
        role="status"
        aria-busy="true"
        aria-label={label}
        {...rest}
      >
        <span className="skeleton-sr-only">{label}</span>
        <Skeleton width={140} height={18} radius={4} />
        <div className="app-skeleton-page-header">
          <div className="app-skeleton-page-header-titles">
            <Skeleton width={230} height={32} radius={6} />
            <Skeleton width="min(360px, 100%)" height={14} radius={4} />
          </div>
        </div>
        <div className="app-skeleton-expense-editor-grid">
          <div className="app-skeleton-document-main">
            <div className="app-skeleton-card-panel">
              <Skeleton width={150} height={20} radius={5} />
              <div className="app-skeleton-document-fields">
                {Array.from({ length: 6 }, (_, index) => (
                  <div className="app-skeleton-field" key={index}>
                    <Skeleton width="42%" height={12} radius={4} />
                    <Skeleton width="100%" height={40} radius={8} />
                  </div>
                ))}
              </div>
            </div>
            <div className="app-skeleton-card-panel">
              <Skeleton width={170} height={20} radius={5} />
              <Skeleton width="100%" height={112} radius={10} />
              <Skeleton width="58%" height={12} radius={4} />
            </div>
            <div className="app-skeleton-card-panel">
              <Skeleton width={120} height={20} radius={5} />
              <Skeleton width="100%" height={92} radius={10} />
            </div>
          </div>
          <aside className="app-skeleton-document-sidebar">
            <div className="app-skeleton-card-panel">
              <Skeleton width="58%" height={20} radius={5} />
              {[0, 1, 2].map((index) => (
                <div className="app-skeleton-summary-row" key={index}>
                  <Skeleton width="52%" height={12} radius={4} />
                  <Skeleton width="34%" height={15} radius={4} />
                </div>
              ))}
            </div>
            <Skeleton width="100%" height={40} radius={10} />
            <Skeleton width="100%" height={68} radius={10} />
          </aside>
        </div>
      </div>
    );
  }

  if (type === "detail" && variant === "expense") {
    return (
      <div
        className={`app-skeleton-page app-skeleton-expense-page ${className}`.trim()}
        style={style}
        role="status"
        aria-busy="true"
        aria-label={label}
        {...rest}
      >
        <span className="skeleton-sr-only">{label}</span>
        <Skeleton width={140} height={18} radius={4} />
        <div className="app-skeleton-page-header">
          <div className="app-skeleton-page-header-titles">
            <Skeleton width={220} height={32} radius={6} />
            <Skeleton width={280} height={14} radius={4} />
            <div className="app-skeleton-inline-actions">
              {[0, 1, 2].map((index) => (
                <Skeleton key={index} width={88} height={24} radius={999} />
              ))}
            </div>
          </div>
          <div className="app-skeleton-page-header-actions">
            <Skeleton width={116} height={40} radius={10} />
            <Skeleton width={132} height={40} radius={10} />
          </div>
        </div>
        <div className="app-skeleton-expense-detail-grid">
          <div className="app-skeleton-document-main">
            <div className="app-skeleton-card-panel">
              <Skeleton width={155} height={20} radius={5} />
              <Skeleton width={190} height={17} radius={4} />
              <div className="app-skeleton-document-meta-grid">
                {Array.from({ length: 4 }, (_, index) => (
                  <div className="app-skeleton-field" key={index}>
                    <Skeleton width="46%" height={11} radius={4} />
                    <Skeleton width="72%" height={15} radius={4} />
                  </div>
                ))}
              </div>
              {[0, 1, 2].map((index) => (
                <div className="app-skeleton-summary-row" key={index}>
                  <Skeleton width="40%" height={12} radius={4} />
                  <Skeleton width="24%" height={15} radius={4} />
                </div>
              ))}
            </div>
            <div className="app-skeleton-card-panel">
              <Skeleton width={180} height={20} radius={5} />
              <Skeleton width={92} height={24} radius={999} />
              <Skeleton width="68%" height={14} radius={4} />
              <Skeleton width={118} height={40} radius={10} />
            </div>
            <div className="app-skeleton-inline-actions">
              {[0, 1, 2].map((index) => (
                <Skeleton key={index} width={118} height={38} radius={8} />
              ))}
            </div>
            <div className="app-skeleton-card-panel">
              <Skeleton width={170} height={20} radius={5} />
              <div className="app-skeleton-summary-row">
                <Skeleton width="42%" height={12} radius={4} />
                <Skeleton width="24%" height={17} radius={4} />
              </div>
              <Skeleton width="72%" height={14} radius={4} />
            </div>
          </div>
          <aside className="app-skeleton-document-sidebar">
            <CardSkeleton variant="summary" />
            <div className="app-skeleton-card-panel">
              <Skeleton width="58%" height={20} radius={5} />
              {[0, 1, 2].map((index) => (
                <div className="app-skeleton-summary-row" key={index}>
                  <Skeleton width="52%" height={12} radius={4} />
                  <Skeleton width="34%" height={15} radius={4} />
                </div>
              ))}
              <Skeleton width="100%" height={40} radius={10} />
            </div>
          </aside>
        </div>
      </div>
    );
  }

  if (type === "editor" && variant === "document") {
    return (
      <div
        className={`app-skeleton-page app-skeleton-document-page ${className}`.trim()}
        style={style}
        role="status"
        aria-busy="true"
        aria-label={label}
        {...rest}
      >
        <span className="skeleton-sr-only">{label}</span>
        <Skeleton width={140} height={18} radius={4} />
        <div className="app-skeleton-page-header">
          <div className="app-skeleton-page-header-titles">
            <Skeleton width={240} height={32} radius={6} />
            <Skeleton width="min(390px, 100%)" height={14} radius={4} />
          </div>
          <div className="app-skeleton-page-header-actions">
            <Skeleton width={112} height={40} radius={10} />
            <Skeleton width={126} height={40} radius={10} />
          </div>
        </div>
        <div className="app-skeleton-document-editor-grid">
          <div className="app-skeleton-document-main">
            <div className="app-skeleton-card-panel">
              <Skeleton width={220} height={22} radius={5} />
              <Skeleton width="min(360px, 80%)" height={12} radius={4} />
              <div className="app-skeleton-document-fields">
                {Array.from({ length: 6 }, (_, index) => (
                  <div className="app-skeleton-field" key={index}>
                    <Skeleton width="42%" height={12} radius={4} />
                    <Skeleton width="100%" height={40} radius={8} />
                  </div>
                ))}
              </div>
            </div>
            <div className="app-skeleton-card-panel">
              <div className="app-skeleton-card-heading-row">
                <div>
                  <Skeleton width={155} height={22} radius={5} />
                  <Skeleton width={260} height={12} radius={4} />
                </div>
                <Skeleton width={120} height={40} radius={10} />
              </div>
              <TableSkeleton columns={5} rows={2} hasHeader hasActions />
              <div className="app-skeleton-inline-actions">
                <Skeleton width={104} height={40} radius={10} />
                <Skeleton width={172} height={40} radius={10} />
              </div>
            </div>
            <div className="app-skeleton-card-panel">
              <Skeleton width={150} height={22} radius={5} />
              <div className="app-skeleton-document-fields">
                {[0, 1].map((index) => (
                  <div className="app-skeleton-field" key={index}>
                    <Skeleton width="38%" height={12} radius={4} />
                    <Skeleton width="100%" height={84} radius={10} />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <aside className="app-skeleton-document-sidebar">
            <CardSkeleton variant="summary" />
            <div className="app-skeleton-document-actions">
              <Skeleton width="100%" height={40} radius={10} />
              <Skeleton width="100%" height={40} radius={10} />
              <Skeleton width="100%" height={40} radius={10} />
            </div>
            <div className="app-skeleton-card-panel">
              <Skeleton width="52%" height={20} radius={5} />
              {[0, 1, 2, 3].map((index) => (
                <div className="app-skeleton-summary-row" key={index}>
                  <Skeleton width="48%" height={12} radius={4} />
                  <Skeleton width="32%" height={14} radius={4} />
                </div>
              ))}
            </div>
          </aside>
        </div>
      </div>
    );
  }

  if (type === "detail" && variant === "document") {
    return (
      <div
        className={`app-skeleton-page app-skeleton-document-page ${className}`.trim()}
        style={style}
        role="status"
        aria-busy="true"
        aria-label={label}
        {...rest}
      >
        <span className="skeleton-sr-only">{label}</span>
        <Skeleton width={140} height={18} radius={4} />
        <div className="app-skeleton-page-header">
          <div className="app-skeleton-page-header-titles">
            <Skeleton width={230} height={32} radius={6} />
            <div className="app-skeleton-inline-actions">
              <Skeleton width={105} height={24} radius={999} />
              <Skeleton width={145} height={14} radius={4} />
              <Skeleton width={72} height={24} radius={999} />
            </div>
          </div>
          <div className="app-skeleton-page-header-actions">
            <Skeleton width={105} height={40} radius={10} />
            <Skeleton width={120} height={40} radius={10} />
            <Skeleton width={126} height={40} radius={10} />
          </div>
        </div>
        <div className="app-skeleton-document-detail-grid">
          <div className="app-skeleton-document-main">
            <div className="app-skeleton-card-panel">
              <Skeleton width={185} height={22} radius={5} />
              <div className="app-skeleton-document-meta-grid">
                {Array.from({ length: 6 }, (_, index) => (
                  <div className="app-skeleton-field" key={index}>
                    <Skeleton width="44%" height={11} radius={4} />
                    <Skeleton width="72%" height={15} radius={4} />
                  </div>
                ))}
              </div>
              <TableSkeleton columns={5} rows={3} hasHeader />
            </div>
            <div className="app-skeleton-document-bottom-grid">
              {[0, 1].map((index) => (
                <div className="app-skeleton-card-panel" key={index}>
                  <Skeleton width="42%" height={20} radius={5} />
                  <Skeleton width="100%" height={12} radius={4} />
                  <Skeleton width="84%" height={12} radius={4} />
                  <Skeleton width="66%" height={12} radius={4} />
                </div>
              ))}
            </div>
          </div>
          <aside className="app-skeleton-document-sidebar">
            <CardSkeleton variant="summary" />
            <div className="app-skeleton-card-panel">
              <Skeleton width="58%" height={20} radius={5} />
              {[0, 1, 2, 3].map((index) => (
                <div className="app-skeleton-summary-row" key={index}>
                  <Skeleton width="46%" height={12} radius={4} />
                  <Skeleton width="38%" height={14} radius={4} />
                </div>
              ))}
              <Skeleton width="100%" height={40} radius={10} />
            </div>
          </aside>
        </div>
      </div>
    );
  }

  if (type === "history") {
    return (
      <div
        className={`app-skeleton-page app-skeleton-history ${className}`.trim()}
        style={style}
        role="status"
        aria-busy="true"
        aria-label={label}
        {...rest}
      >
        <span className="skeleton-sr-only">{label}</span>
        <div className="app-skeleton-history-header">
          <div className="app-skeleton-page-header-titles">
            <Skeleton width={92} height={14} radius={4} />
            <Skeleton width={240} height={36} radius={6} />
            <Skeleton width="min(440px, 100%)" height={14} radius={4} />
          </div>
          <div className="app-skeleton-history-chip">
            <Skeleton variant="circular" width={34} height={34} />
            <div>
              <Skeleton width={130} height={14} radius={4} />
              <Skeleton width={180} height={12} radius={4} />
            </div>
            <Skeleton variant="pill" width={70} height={26} />
          </div>
        </div>
        <div className="app-skeleton-history-banner">
          <Skeleton variant="circular" width={20} height={20} />
          <div>
            <Skeleton width={160} height={14} radius={4} />
            <Skeleton width="min(420px, 100%)" height={12} radius={4} />
          </div>
        </div>
        <div className="app-skeleton-history-workspace">
          <div className="app-skeleton-history-feed-panel">
            <div className="app-skeleton-history-toolbar">
              <div>
                <Skeleton width={112} height={34} radius={8} />
                <Skeleton width={82} height={34} radius={8} />
              </div>
              <Skeleton width={240} height={38} radius={9} />
            </div>
            <div className="app-skeleton-history-events">
              {Array.from({ length: 4 }, (_, index) => (
                <div className="app-skeleton-history-event" key={index}>
                  <Skeleton variant="circular" width={34} height={34} />
                  <div>
                    <span>
                      <Skeleton width="35%" height={14} radius={4} />
                      <Skeleton width={120} height={12} radius={4} />
                    </span>
                    <Skeleton width="24%" height={12} radius={4} />
                    <Skeleton width="88%" height={14} radius={4} />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="app-skeleton-history-note-panel">
            <Skeleton width={190} height={22} radius={5} />
            <Skeleton width="100%" height={12} radius={4} />
            <Skeleton width="82%" height={12} radius={4} />
            <Skeleton width={44} height={14} radius={4} />
            <Skeleton width="100%" height={150} radius={10} />
            <Skeleton width={110} height={40} radius={10} />
          </div>
        </div>
      </div>
    );
  }

  if (type === "detail") {
    return (
      <div
        className={`app-skeleton-page ${className}`.trim()}
        style={style}
        role="status"
        aria-busy="true"
        aria-label={label}
        {...rest}
      >
        <span className="skeleton-sr-only">{label}</span>
        {/* Back Link */}
        <Skeleton width={140} height={18} radius={4} />

        {/* Hero Header */}
        <div className="app-skeleton-page-header">
          <div className="app-skeleton-page-header-titles">
            <Skeleton width={260} height={32} radius={6} />
            <Skeleton width={380} height={14} radius={4} />
          </div>
          <div className="app-skeleton-page-header-actions">
            <Skeleton width={110} height={38} radius={8} />
            <Skeleton width={130} height={38} radius={8} />
          </div>
        </div>

        {/* Two Column Layout (Document Details & Sidebar Summary) */}
        <div className="app-skeleton-two-col">
          {/* Main Card */}
          <div className="app-skeleton-card-panel" style={{ minHeight: 460 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Skeleton width="40%" height={24} radius={6} />
              <Skeleton variant="pill" width={100} height={26} />
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "16px",
                padding: "12px 0",
              }}
            >
              <div>
                <Skeleton
                  width="45%"
                  height={12}
                  radius={4}
                  style={{ marginBottom: 6 }}
                />
                <Skeleton width="70%" height={16} radius={4} />
              </div>
              <div>
                <Skeleton
                  width="45%"
                  height={12}
                  radius={4}
                  style={{ marginBottom: 6 }}
                />
                <Skeleton width="60%" height={16} radius={4} />
              </div>
            </div>
            <TableSkeleton
              columns={4}
              rows={4}
              hasHeader={true}
              hasActions={false}
            />
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                padding: "16px 0 0",
              }}
            >
              <div
                style={{
                  width: 220,
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                }}
              >
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <Skeleton width={70} height={12} radius={4} />
                  <Skeleton width={60} height={12} radius={4} />
                </div>
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <Skeleton width={80} height={16} radius={4} />
                  <Skeleton width={90} height={20} radius={4} />
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar Cards */}
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <CardSkeleton variant="summary" />
            <div className="app-skeleton-card-panel" style={{ minHeight: 220 }}>
              <Skeleton width="50%" height={18} radius={6} />
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                  marginTop: 8,
                }}
              >
                <Skeleton width="80%" height={12} radius={4} />
                <Skeleton width="65%" height={12} radius={4} />
                <Skeleton width="90%" height={12} radius={4} />
                <Skeleton width="45%" height={12} radius={4} />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (type === "editor") {
    return (
      <div
        className={`app-skeleton-page ${className}`.trim()}
        style={style}
        role="status"
        aria-busy="true"
        aria-label={label}
        {...rest}
      >
        <span className="skeleton-sr-only">{label}</span>
        <Skeleton width={140} height={18} radius={4} />

        <div className="app-skeleton-page-header">
          <div className="app-skeleton-page-header-titles">
            <Skeleton width={240} height={32} radius={6} />
            <Skeleton width={320} height={14} radius={4} />
          </div>
          <div className="app-skeleton-page-header-actions">
            <Skeleton width={100} height={38} radius={8} />
            <Skeleton width={120} height={38} radius={8} />
            <Skeleton width={130} height={38} radius={8} />
          </div>
        </div>

        {/* Form Fields Card */}
        <div className="app-skeleton-card-panel">
          <Skeleton width="30%" height={20} radius={6} />
          <div className="app-skeleton-form-grid">
            {Array.from({ length: 6 }, (_, i) => (
              <div
                key={i}
                style={{ display: "flex", flexDirection: "column", gap: 8 }}
              >
                <Skeleton width="40%" height={12} radius={4} />
                <Skeleton width="100%" height={40} radius={8} />
              </div>
            ))}
          </div>
        </div>

        {/* Line Items Card */}
        <div className="app-skeleton-card-panel">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Skeleton width="25%" height={20} radius={6} />
            <Skeleton width={110} height={34} radius={8} />
          </div>
          <TableSkeleton
            columns={5}
            rows={3}
            hasHeader={true}
            hasActions={true}
          />
        </div>
      </div>
    );
  }

  if (type === "dashboard") {
    return (
      <div
        className={`app-skeleton-page ${className}`.trim()}
        style={style}
        role="status"
        aria-busy="true"
        aria-label={label}
        {...rest}
      >
        <span className="skeleton-sr-only">{label}</span>
        <div className="app-skeleton-page-header">
          <div className="app-skeleton-page-header-titles">
            <Skeleton width={260} height={32} radius={6} />
            <Skeleton width={360} height={14} radius={4} />
          </div>
          <Skeleton width={130} height={42} radius={8} />
        </div>

        <div className="app-skeleton-metrics-grid">
          <CardSkeleton variant="stat" />
          <CardSkeleton variant="stat" />
          <CardSkeleton variant="stat" />
          <CardSkeleton variant="stat" />
        </div>

        <div className="app-skeleton-two-col">
          <CardSkeleton variant="chart" />
          <CardSkeleton variant="chart" />
        </div>

        <TableSkeleton
          columns={5}
          rows={5}
          hasHeader={true}
          hasActions={true}
        />
      </div>
    );
  }

  // Default "register" / list page layout (Invoices, Quotations, Expenses, RCTI, Credit Notes)
  return (
    <div
      className={`app-skeleton-page ${className}`.trim()}
      style={style}
      role="status"
      aria-busy="true"
      aria-label={label}
      {...rest}
    >
      <span className="skeleton-sr-only">{label}</span>
      {/* Page Header */}
      <div className="app-skeleton-page-header">
        <div className="app-skeleton-page-header-titles">
          <Skeleton width={220} height={32} radius={6} />
          <Skeleton width={360} height={14} radius={4} />
        </div>
        <div className="app-skeleton-page-header-actions">
          <Skeleton width={100} height={38} radius={8} />
          <Skeleton width={130} height={38} radius={8} />
        </div>
      </div>

      {/* Metrics Row (3-4 summary cards) */}
      <div className="app-skeleton-metrics-grid">
        <CardSkeleton variant="stat" />
        <CardSkeleton variant="stat" />
        <CardSkeleton variant="stat" />
      </div>

      {/* Filter and Search Bar */}
      <div className="app-skeleton-filter-bar">
        <Skeleton width="min(380px, 100%)" height={40} radius={8} />
        <Skeleton width={160} height={40} radius={8} />
        <Skeleton width={180} height={40} radius={8} />
        <Skeleton width={100} height={40} radius={8} />
      </div>

      {/* Register Table */}
      <TableSkeleton
        columns={6}
        rows={7}
        hasHeader={true}
        hasCheckbox={true}
        hasActions={true}
      />
    </div>
  );
}

PageSkeleton.propTypes = {
  type: PropTypes.oneOf([
    "register",
    "detail",
    "editor",
    "dashboard",
    "history",
  ]),
  variant: PropTypes.oneOf(["default", "document", "expense", "contact"]),
  className: PropTypes.string,
  style: PropTypes.object,
  label: PropTypes.string,
};
